import type {
  AdmDocumentContext,
  AdmFileUploadResponse,
  AdmCreatedDocumentResponse,
  AdmTagValuePair,
} from "@/types/adm-document";
import {
  ADM_DOC_TYPE_ID,
  ADM_FOLDER_ID,
  ADM_TAG_IDS,
} from "@/types/adm-document";
import { getApiBaseUrl } from "@/lib/data-service";
import { authFetch } from "@/lib/auth-fetch";

export interface AdmInvoiceMetadata {
  invoiceNumber: string;
  documentDate?: string;
  description?: string;
  accountCode?: string;
  accountName?: string;
  transactionType?: string;
}

export async function getAdmAuthHeaders(
  getAccessToken?: () => Promise<string | undefined>,
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (getAccessToken) {
    const token = await getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }
  return headers;
}

function buildInvoiceTagValues(
  ctx: AdmDocumentContext,
  metadata: AdmInvoiceMetadata,
): AdmTagValuePair[] {
  // DocType 29's stored procedure references @TAG_1, @TAG_3, @TAG_4, @TAG_99
  // etc. — variables are only declared for TagIds present in the request.
  // All tags the proc references MUST be sent (even with empty values) or the
  // ADM backend returns "Must declare the scalar variable @TAG_N".
  const accountCodeValue = metadata.accountCode || metadata.accountName || "";
  const tags: AdmTagValuePair[] = [
    { TagId: ADM_TAG_IDS.DATABASE_NAME, TagValue: ctx.dbName },
    { TagId: ADM_TAG_IDS.SALES_LEDGER_PRIMARY, TagValue: accountCodeValue },
    { TagId: ADM_TAG_IDS.HEADER_REF, TagValue: metadata.invoiceNumber || "" },
    { TagId: ADM_TAG_IDS.ACCOUNT_CODE, TagValue: metadata.accountCode || accountCodeValue },
    { TagId: ADM_TAG_IDS.ACCOUNT_NAME, TagValue: metadata.accountName || "" },
    { TagId: ADM_TAG_IDS.TRANSACTION_DATE, TagValue: metadata.documentDate || "" },
    { TagId: ADM_TAG_IDS.DESCRIPTION, TagValue: metadata.description || "" },
    { TagId: ADM_TAG_IDS.TRANSACTION_TYPE, TagValue: metadata.transactionType || "" },
  ];

  return tags;
}

/**
 * Upload an invoice PDF to Access Document Management (ADM) using the user's token.
 * Two-step process: FileUpload (get ServerFileKey) then Documents (create document record).
 *
 * @returns The ADM document ID on success
 */
export async function uploadInvoicePdfToAdm(
  ctx: AdmDocumentContext,
  getAccessToken: (() => Promise<string | undefined>) | undefined,
  pdfBlob: Blob,
  metadata: AdmInvoiceMetadata,
): Promise<string> {
  // Step 1: Upload the file
  const uploadHeaders = await getAdmAuthHeaders(getAccessToken);
  delete uploadHeaders["Content-Type"];

  const formData = new FormData();
  const fileName = `${metadata.invoiceNumber}.pdf`;
  formData.append("file", pdfBlob, fileName);

  const uploadResponse = await fetch(
    `${ctx.instanceUrl}/FileUpload`,
    { method: "POST", headers: uploadHeaders, body: formData },
  );

  if (!uploadResponse.ok) {
    throw new Error(`ADM FileUpload failed (HTTP ${uploadResponse.status})`);
  }

  const uploadJson: AdmFileUploadResponse = await uploadResponse.json();
  const identifiers = uploadJson.Identifiers ?? [];
  if (identifiers.length === 0) {
    throw new Error("ADM FileUpload returned no file entries");
  }

  const serverFileKey = identifiers[0].ServerFileKey;

  // Step 2: Create the document with tags
  const createHeaders = await getAdmAuthHeaders(getAccessToken);
  const tagValues = buildInvoiceTagValues(ctx, metadata);
  const titleWithoutExt = metadata.invoiceNumber;

  const createBody = {
    ServerFileKey: serverFileKey,
    Attributes: {
      DestinationFolderId: ADM_FOLDER_ID,
      Title: titleWithoutExt,
    },
    IndexingInfo: {
      DocTypeId: ADM_DOC_TYPE_ID,
      TagValues: tagValues,
    },
  };

  const createResponse = await fetch(
    `${ctx.instanceUrl}/Documents`,
    { method: "POST", headers: createHeaders, body: JSON.stringify(createBody) },
  );

  if (!createResponse.ok) {
    throw new Error(`ADM CreateDocument failed (HTTP ${createResponse.status})`);
  }

  const created: AdmCreatedDocumentResponse = await createResponse.json();
  return String(created.Id);
}

/**
 * Store the ADM document ID on the invoice record in the backend.
 */
export async function storeAdmDocumentId(
  invoiceId: number | string,
  admDocumentId: string,
): Promise<void> {
  await authFetch(`${getApiBaseUrl()}/billing/invoices/${invoiceId}/adm-document-id`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ admDocumentId }),
  });
}
